import { t } from "@/i18n-js/instance";
import { Collapsible } from "@circle-react-uikit/Collapsible";
import { Form } from "@circle-react-uikit/Form";
import { AlignmentField } from "../../../../Editor/fields/AlignmentField";
import { SliderField } from "../../../../Editor/fields/SliderField";
import { useCustomColors } from "../../../../ThemeConfiguration/hooks/useCustomColors";
import type { ButtonVariant } from "./ButtonStateSelector";
import { generateFormItemName } from "./Styles";
import {
  FONT_STYLE_OPTIONS,
  FONT_WEIGHT_OPTIONS,
  TEXT_ALIGN_OPTIONS,
  TEXT_DECORATION_OPTIONS,
  TEXT_TRANSFORM_OPTIONS,
} from "./constants";
import type { ButtonType } from "./constants";

interface TextProps {
  buttonType: ButtonType;
  selectedButtonVariant: ButtonVariant;
}

export const Text = ({ selectedButtonVariant, buttonType }: TextProps) => {
  const { flattenedCssVariables } = useCustomColors();

  return (
    <Collapsible title={t("builder_ui.prop_type.styles.text")} defaultOpen>
      <div className="px-6 py-5">
        <Form.Item
          name={generateFormItemName(
            buttonType,
            selectedButtonVariant,
            "color",
          )}
          label={t("builder_ui.prop_type.styles.color")}
          hideDescription
          hideBorder
          className="!flex !w-full !items-center !justify-between !p-0"
        >
          <Form.ColorPickerV2
            name={generateFormItemName(
              buttonType,
              selectedButtonVariant,
              "color",
            )}
            cssVariables={flattenedCssVariables}
            shouldResolveCssVariable
          />
        </Form.Item>
        <div className="mt-5">
          <SliderField
            name={generateFormItemName(
              buttonType,
              selectedButtonVariant,
              "fontSize",
            )}
            label={t("builder_ui.prop_type.styles.font_size")}
            units={["rem", "px"]}
            defaultUnit="rem"
            step={1}
            min={0}
            max={500}
          />
        </div>

        <Form.Item
          name={generateFormItemName(
            buttonType,
            selectedButtonVariant,
            "fontStyle",
          )}
          label={t("builder_ui.prop_type.styles.font_style")}
          hideDescription
          hideBorder
        >
          <Form.SelectNative
            name={generateFormItemName(
              buttonType,
              selectedButtonVariant,
              "fontStyle",
            )}
            options={FONT_STYLE_OPTIONS}
          />
        </Form.Item>

        <Form.Item
          name={generateFormItemName(
            buttonType,
            selectedButtonVariant,
            "fontWeight",
          )}
          label={t("builder_ui.prop_type.styles.font_weight")}
          hideDescription
          hideBorder
        >
          <Form.SelectNative
            name={generateFormItemName(
              buttonType,
              selectedButtonVariant,
              "fontWeight",
            )}
            options={FONT_WEIGHT_OPTIONS}
          />
        </Form.Item>

        <Form.Item
          name={generateFormItemName(
            buttonType,
            selectedButtonVariant,
            "lineHeight",
          )}
          label={t("builder_ui.prop_type.styles.line_height")}
          hideDescription
          hideBorder
        >
          <Form.Input
            name={generateFormItemName(
              buttonType,
              selectedButtonVariant,
              "lineHeight",
            )}
            type="number"
            min={0}
            max={5}
            step={0.1}
          />
        </Form.Item>

        <div className="mt-5">
          <AlignmentField
            label={t("builder_ui.prop_type.styles.text_align")}
            name={generateFormItemName(
              buttonType,
              selectedButtonVariant,
              "textAlign",
            )}
            options={TEXT_ALIGN_OPTIONS}
          />
        </div>

        <Form.Item
          name={generateFormItemName(
            buttonType,
            selectedButtonVariant,
            "textDecoration",
          )}
          label={t("builder_ui.prop_type.styles.text_decoration")}
          hideDescription
          hideBorder
        >
          <Form.SelectNative
            name={generateFormItemName(
              buttonType,
              selectedButtonVariant,
              "textDecoration",
            )}
            options={TEXT_DECORATION_OPTIONS}
          />
        </Form.Item>

        <Form.Item
          name={generateFormItemName(
            buttonType,
            selectedButtonVariant,
            "textTransform",
          )}
          label={t("builder_ui.prop_type.styles.text_transform")}
          hideDescription
          hideBorder
        >
          <Form.SelectNative
            name={generateFormItemName(
              buttonType,
              selectedButtonVariant,
              "textTransform",
            )}
            options={TEXT_TRANSFORM_OPTIONS}
          />
        </Form.Item>
      </div>
    </Collapsible>
  );
};
